import { useEffect, useRef, useState } from 'react';
import DarkModeToggle from './components/DarkModeToggle';
import { useDarkMode } from './hooks/useDarkMode';
import { useIsMobile } from './hooks/useIsMobile';
import { getColors } from './theme';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const STORE_KEY = 'spandan-calendar-entries';

type Entries = Record<string, string>;

const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
const isoOf = (d: Date) => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
const parseIso = (s: string) => {
  const p = s.split('-');
  return new Date(+p[0], +p[1] - 1, +p[2]);
};

// Storage layer — swap these two for API calls (GET/POST /api/logs) to move off localStorage.
function loadEntries(): Entries {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || '{}') || {};
  } catch {
    return {};
  }
}
function persistEntries(entries: Entries) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(entries));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

export default function Calendar() {
  const [dark, setDark] = useDarkMode();
  const mobile = useIsMobile();

  const [view, setView] = useState<'log' | 'overview'>('log');
  const [selectedDate, setSelectedDate] = useState(() => isoOf(new Date()));
  const [draft, setDraft] = useState('');
  const [entries, setEntries] = useState<Entries>({});
  const [savedNote, setSavedNote] = useState('');
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth());

  const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const noteTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const dateInput = useRef<HTMLInputElement>(null);

  const todayIso = isoOf(new Date());

  useEffect(() => {
    const loaded = loadEntries();
    setEntries(loaded);
    setDraft(loaded[isoOf(new Date())] || '');
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (noteTimer.current) clearTimeout(noteTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function saveDraft(text: string, date: string) {
    setEntries(prev => {
      const next = { ...prev };
      if (text.trim()) next[date] = text;
      else delete next[date];
      persistEntries(next);
      return next;
    });
    setSavedNote('Saved');
    if (noteTimer.current) clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(() => setSavedNote(''), 1600);
  }

  function flushPending() {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = undefined;
      saveDraft(draft, selectedDate);
    }
  }

  function onDraftInput(text: string) {
    setDraft(text);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveDraft(text, selectedDate), 500);
  }

  function selectDate(iso: string) {
    flushPending();
    setSelectedDate(iso);
    setDraft(entries[iso] || '');
    setView('log');
    setSavedNote('');
  }

  function goOverview() {
    flushPending();
    const d = parseIso(selectedDate);
    setViewMonth(d.getMonth());
    setViewYear(d.getFullYear());
    setView('overview');
  }

  function stepMonth(delta: number) {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) {
      m = 11;
      y--;
    }
    if (m > 11) {
      m = 0;
      y++;
    }
    setViewMonth(m);
    setViewYear(y);
  }

  function openPicker() {
    const el = dateInput.current;
    if (!el) return;
    try {
      // showPicker is not in older TS lib DOM typings
      (el as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
    } catch {
      el.focus();
      el.click();
    }
  }

  const c = getColors(dark);
  const todayRing = dark ? '#3fb950' : '#2f9e44';
  const font = "'0xProto', 'JetBrains Mono', monospace";
  const serif = "'0xProto', 'JetBrains Mono', monospace";

  const selDateObj = parseIso(selectedDate);
  const selectedTitle = selectedDate === todayIso ? 'Today' : DAYS_FULL[selDateObj.getDay()];
  const selectedDateLabel = MONTHS[selDateObj.getMonth()] + ' ' + selDateObj.getDate() + ', ' + selDateObj.getFullYear();
  const draftPlaceholder = selectedDate === todayIso ? 'What did you get done today?' : 'What happened on this day?';

  const recentKeys = Object.keys(entries)
    .filter(k => entries[k] && entries[k].trim())
    .sort((a, b) => b.localeCompare(a))
    .slice(0, 5);

  // ---- overview grid ----
  const monthTitle = MONTHS[viewMonth] + ' ' + viewYear;
  const startOffset = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;

  const overviewLinkStyle: React.CSSProperties = {
    fontSize: 14, color: c.fg, textDecoration: 'none', fontWeight: 500,
    borderBottom: `1px solid ${c.fg}`, paddingBottom: 2, whiteSpace: 'nowrap',
  };
  const logFootRowStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, gap: 16,
  };

  return (
    <div
      className="cal-scroll"
      style={{
        background: c.bg, color: c.fg, fontFamily: font,
        transition: 'background .35s ease, color .35s ease',
        minHeight: 'var(--vh100)', overflowY: 'auto', position: 'relative',
        ['--cal-scrollbar' as string]: c.hair, ['--cal-muted' as string]: c.muted,
      }}
    >
      <style>{`
        @keyframes cal-riseIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        @keyframes cal-fade { from { opacity: 0; } to { opacity: 1; } }
        .cal-cell { transition: background .2s ease, border-color .2s ease, transform .18s cubic-bezier(.2,.7,.2,1); }
        .cal-lk { transition: transform .28s cubic-bezier(.2,.7,.2,1); }
        /* :hover sticks after a tap on touch devices, so gate it on a real pointer */
        @media (hover: hover) {
          .cal-cell:hover { transform: translateY(-2px); }
          .cal-lk:hover { transform: translateX(6px); }
        }
        .cal-scroll::-webkit-scrollbar { width: 6px; }
        .cal-scroll::-webkit-scrollbar-thumb { background: var(--cal-scrollbar); border-radius: 3px; }
        .cal-textarea::placeholder { color: var(--cal-muted); opacity: 0.7; }
      `}</style>

      <DarkModeToggle dark={dark} onToggle={() => setDark(d => !d)} borderColor={c.hair} color={c.fg} />

      <main
        style={{
          maxWidth: view === 'overview' ? 760 : 640, margin: '0 auto',
          padding: mobile
            ? `76px calc(20px + env(safe-area-inset-right, 0px)) calc(64px + env(safe-area-inset-bottom, 0px)) calc(20px + env(safe-area-inset-left, 0px))`
            : '88px 48px 96px',
          minHeight: 'var(--vh100)', boxSizing: 'border-box',
        }}
      >
        <a
          href="/"
          className="cal-lk"
          style={{
            display: 'inline-block', marginBottom: mobile ? 40 : 56, fontSize: 13,
            color: c.muted, textDecoration: 'none', letterSpacing: '0.01em',
          }}
        >
          ← Spandan Shrestha
        </a>

        {view === 'log' && (
          <section style={{ animation: 'cal-fade .4s ease both' }}>
            <h1
              style={{
                margin: '0 0 20px', fontFamily: serif, fontWeight: 500,
                fontSize: mobile ? 30 : 40, letterSpacing: '0.01em', lineHeight: 1.1,
                animation: 'cal-riseIn .6s cubic-bezier(.2,.7,.2,1) both',
              }}
            >
              {selectedTitle}
            </h1>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
              <button
                onClick={openPicker}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px',
                  background: c.cardBg, border: `1px solid ${c.cardBorder}`, borderRadius: 999,
                  color: c.fg, fontSize: 13, fontFamily: font, cursor: 'pointer', whiteSpace: 'nowrap',
                  transition: 'border-color .25s ease, background .35s ease',
                }}
              >
                <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
                  <rect x={3} y={4} width={18} height={18} rx={2} />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
                {selectedDateLabel}
              </button>
              <input
                ref={dateInput}
                type="date"
                value={selectedDate}
                onChange={e => e.target.value && selectDate(e.target.value)}
                style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
              />
              {selectedDate !== todayIso && (
                <button
                  onClick={() => selectDate(todayIso)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', fontFamily: font,
                    fontSize: 12, color: c.muted, borderBottom: `1px solid ${c.hair}`, padding: '0 0 2px',
                  }}
                >
                  Jump to today
                </button>
              )}
            </div>

            <textarea
              className="cal-textarea"
              value={draft}
              onInput={e => onDraftInput((e.target as HTMLTextAreaElement).value)}
              onChange={e => onDraftInput(e.target.value)}
              placeholder={draftPlaceholder}
              rows={9}
              style={{
                width: '100%', boxSizing: 'border-box', padding: mobile ? '16px' : '20px 22px',
                background: c.cardBg, border: `1px solid ${c.cardBorder}`, borderRadius: 12,
                color: c.fg, fontFamily: font, fontSize: 16, lineHeight: 1.7, resize: 'vertical',
                outline: 'none', minHeight: 180, transition: 'border-color .25s ease, background .35s ease',
              }}
            />

            <div style={logFootRowStyle}>
              <span
                style={{
                  fontSize: 12, color: todayRing, minHeight: 16, transition: 'opacity .3s ease',
                  fontFamily: "'0xProto', 'JetBrains Mono', monospace",
                }}
              >
                {savedNote}
              </span>
              <a
                href="#overview"
                onClick={e => {
                  e.preventDefault();
                  goOverview();
                }}
                className="cal-lk"
                style={overviewLinkStyle}
              >
                View calendar →
              </a>
            </div>

            {recentKeys.length > 0 && (
              <div style={{ marginTop: mobile ? 48 : 64 }}>
                <span style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: c.muted, display: 'block', marginBottom: 4 }}>
                  Recent
                </span>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {recentKeys.map(k => {
                    const d = parseIso(k);
                    const label = k === todayIso ? 'Today' : `${WEEKDAYS[(d.getDay() + 6) % 7]} ${MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}`;
                    return (
                      <button
                        key={k}
                        onClick={() => selectDate(k)}
                        style={{
                          display: 'flex', gap: mobile ? 10 : 16, alignItems: 'baseline', textAlign: 'left', width: '100%',
                          background: 'none', border: 'none', borderTop: `1px solid ${c.hair}`, cursor: 'pointer',
                          padding: '14px 2px', fontFamily: font,
                        }}
                      >
                        <span style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: c.muted, width: mobile ? 74 : 96, flexShrink: 0 }}>
                          {label}
                        </span>
                        <span style={{ fontSize: 14, color: c.fg, lineHeight: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                          {entries[k].replace(/\s+/g, ' ').trim()}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        )}

        {view === 'overview' && (
          <section style={{ animation: 'cal-fade .4s ease both' }}>
            <div style={{ margin: '0 0 28px', display: 'flex', alignItems: 'center', gap: 11 }}>
              <span style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: c.muted }}>
                Calendar Overview
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, gap: 12 }}>
              <button
                onClick={() => stepMonth(-1)}
                aria-label="Previous month"
                style={{
                  width: 44, height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${c.hair}`, borderRadius: '50%', background: c.bg, color: c.fg,
                  cursor: 'pointer', fontSize: 16, transition: 'border-color .25s ease',
                }}
              >
                ←
              </button>
              <h1 style={{ margin: 0, fontFamily: serif, fontWeight: 500, fontSize: mobile ? 24 : 32, letterSpacing: '0.01em' }}>
                {monthTitle}
              </h1>
              <button
                onClick={() => stepMonth(1)}
                aria-label="Next month"
                style={{
                  width: 44, height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${c.hair}`, borderRadius: '50%', background: c.bg, color: c.fg,
                  cursor: 'pointer', fontSize: 16, transition: 'border-color .25s ease',
                }}
              >
                →
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: mobile ? 5 : 8, marginBottom: 8 }}>
              {WEEKDAYS.map(d => (
                <span key={d} style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.muted, textAlign: 'center', paddingBottom: 2 }}>
                  {d}
                </span>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: mobile ? 5 : 8 }}>
              {Array.from({ length: totalCells }, (_, i) => {
                const dayNum = i - startOffset + 1;
                const inMonth = dayNum >= 1 && dayNum <= daysInMonth;
                const iso = inMonth ? `${viewYear}-${pad(viewMonth + 1)}-${pad(dayNum)}` : null;
                const entry = iso && entries[iso] && entries[iso].trim() ? entries[iso] : null;
                const isTd = iso === todayIso;
                const isSel = iso === selectedDate;
                return (
                  <button
                    key={i}
                    onClick={() => inMonth && iso && selectDate(iso)}
                    className="cal-cell"
                    style={{
                      position: 'relative', boxSizing: 'border-box',
                      minHeight: mobile ? 48 : 92, padding: mobile ? '5px 4px' : '8px 9px',
                      display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left',
                      background: entry ? c.cardBg : 'transparent',
                      border: `1px solid ${isSel ? c.fg : c.cardBorder}`,
                      borderRadius: 9, cursor: inMonth ? 'pointer' : 'default',
                      opacity: inMonth ? 1 : 0.28, fontFamily: font,
                      boxShadow: isTd ? `inset 0 0 0 1.5px ${todayRing}` : 'none',
                    }}
                  >
                    <span style={{ fontSize: 12, fontWeight: isTd ? 600 : 400, color: isTd ? todayRing : entry ? c.fg : c.muted, fontVariantNumeric: 'tabular-nums' }}>
                      {inMonth ? dayNum : ''}
                    </span>
                    {/* a phone cell is ~43px wide: an entry excerpt there is
                        illegible, so mobile gets a presence dot instead */}
                    {entry && (mobile ? (
                      <span
                        style={{
                          width: 4, height: 4, borderRadius: '50%',
                          background: c.accent, alignSelf: 'center', marginTop: 'auto',
                          marginBottom: 2, flexShrink: 0,
                        }}
                      />
                    ) : (
                      <span
                        style={{
                          fontSize: 11, lineHeight: 1.35, color: c.muted,
                          overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {entry.replace(/\s+/g, ' ').trim()}
                      </span>
                    ))}
                  </button>
                );
              })}
            </div>

            <div style={logFootRowStyle}>
              <a
                href="#log"
                onClick={e => {
                  e.preventDefault();
                  setView('log');
                }}
                className="cal-lk"
                style={overviewLinkStyle}
              >
                ← Back to log
              </a>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
